import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../context/I18nContext';
import { useCategorySelect } from '../../hooks/useCategorySelect';
import SelectWithAdd from '../ui/SelectWithAdd';
import ConfirmModal from '../ui/ConfirmModal';
import type { CategorySelectType } from '../../hooks/useCategorySelect';

interface CategorySelectWithAddProps {
  type: CategorySelectType;
  value: string;
  onChange: (value: string) => void;
  label: string;
  error?: string;
  addPlaceholder: string;
  /** Called when a default is available and value is empty (e.g. to init form on create). */
  onDefaultReady?: (defaultId: number) => void;
  /** Called after a category delete that migrated leads (so parent can refresh leads list). */
  onDeleteWithMigrationComplete?: () => void;
}

/**
 * Dropdown for a category type (contact_type or sector) with add/delete,
 * same UX for both: create custom categories, delete with confirm and migrate leads.
 */
const CategorySelectWithAdd: React.FC<CategorySelectWithAddProps> = ({
  type,
  value,
  onChange,
  label,
  error,
  addPlaceholder,
  onDefaultReady,
  onDeleteWithMigrationComplete,
}) => {
  const { t } = useI18n();
  const {
    options,
    loading,
    defaultCategory,
    replacementCategoryWhenDeleted,
    createCategory,
    countUsed,
    performDeleteWithMigrate,
    isCustomOption,
    placeholderLabel,
  } = useCategorySelect(type);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingDeleteCount, setPendingDeleteCount] = useState(0);
  const hasReportedDefaultRef = useRef(false);

  const displayValue = value || defaultCategory?.id?.toString() || '';

  useEffect(() => {
    if (value === '') hasReportedDefaultRef.current = false;
  }, [value]);

  // When default becomes available and value is empty, notify parent (e.g. to init form on create)
  useEffect(() => {
    if (!onDefaultReady || value !== '' || !defaultCategory) return;
    if (hasReportedDefaultRef.current) return;
    hasReportedDefaultRef.current = true;
    onDefaultReady(defaultCategory.id);
  }, [onDefaultReady, value, defaultCategory]);

  const handleAddOption = async (label: string) => {
    const newCategory = await createCategory(label);
    onChange(newCategory.id.toString());
  };

  const handleDeleteOption = async (optionValue: string) => {
    const id = parseInt(optionValue, 10);
    const count = await countUsed(id);
    if (count > 0) {
      setPendingDeleteId(optionValue);
      setPendingDeleteCount(count);
      setConfirmOpen(true);
      return;
    }
    await performDeleteWithMigrate(id);
    const newValue = value === optionValue
      ? (replacementCategoryWhenDeleted?.id.toString() ?? '')
      : value;
    if (value !== newValue) onChange(newValue);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = parseInt(pendingDeleteId, 10);
    const hadLeadsToMigrate = pendingDeleteCount > 0;
    try {
      await performDeleteWithMigrate(id);
      const newValue = value === pendingDeleteId
        ? (replacementCategoryWhenDeleted?.id.toString() ?? '')
        : value;
      if (value !== newValue) onChange(newValue);
      if (hadLeadsToMigrate) onDeleteWithMigrationComplete?.();
    } catch (err) {
      console.error('Error deleting category:', err);
    } finally {
      setConfirmOpen(false);
      setPendingDeleteId(null);
      setPendingDeleteCount(0);
    }
  };

  const confirmMessage =
    type === 'contact_type'
      ? t('leads.confirmDeleteCategory').replace('{count}', String(pendingDeleteCount))
      : t('leads.confirmDeleteSector').replace('{count}', String(pendingDeleteCount));

  return (
    <>
      <SelectWithAdd
        label={label}
        value={displayValue}
        onChange={onChange}
        options={options}
        placeholder={placeholderLabel}
        error={error}
        onAddOption={handleAddOption}
        onDeleteOption={handleDeleteOption}
        addButtonText={t('common.add')}
        addPlaceholder={addPlaceholder}
        isCustomOption={isCustomOption}
        loading={loading}
      />
      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setPendingDeleteId(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t('common.confirm')}
        message={confirmMessage}
        confirmText={t('common.delete')}
        variant="danger"
      />
    </>
  );
};

export default CategorySelectWithAdd;
